import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { createCheckout } from '../services/billingService';
import { takeBillingIntent } from '../services/billingIntent';
import { fetchAppConfig, getCachedRolesEnabled } from '../services/appConfigService';
import { requestAccount } from '../util/authEvents';
import type { TierValue } from '../constants/tiers';

// User type matching Laravel /api/me response
export type User = {
  id: number;
  name: string;
  email: string;
  email_verified_at: string | null;
  is_admin?: boolean; // present only for admins; omitted entirely for non-admins
  /**
   * Admins only. `tier` above is the tier the server ACTS ON; these two say why.
   * `acting_tier` is null until an admin opts in — admin grants access, not entitlement.
   */
  real_tier?: TierValue;
  acting_tier?: TierValue | null;
  /**
   * The server is treating this session as a visitor with no account (an admin acting as
   * Guest). Sent explicitly rather than inferred, because the payload still carries
   * `is_admin`/`acting_tier` so the admin can reach the picker to switch back — so "there is a
   * user object" does NOT mean "signed in" here.
   */
  acting_anonymously?: boolean;
  tier: TierValue;
  limits: {
    // Shared abuse ceiling, identical on every tier — not a plan feature. null = no ceiling.
    max_slices_per_image: number | null;
    max_file_size_bytes: number;
    // null = unlimited. Listing caps were removed as a plan lever, so this is null on every tier
    // today; the field stays because the server-side mechanism does.
    max_active_projects: number | null;
    // How long work is kept. null = kept while subscribed (Pro), which is the plan model:
    // Anonymous is transient, a free account buys two weeks, Pro has no expiry.
    retention_hours: number | null;
  };
  features: {
    save_projects: boolean;
    history_depth: number | null;  // null = unlimited
    teams: boolean;
    snap_mode: boolean;
    history: boolean;
    reedit_projects: boolean;
    layers: boolean;
    autosave_drafts: boolean;
  };
  subscription: Subscription | null;
};

export type SubscriptionState = 'active' | 'trialing' | 'canceling' | 'past_due' | 'incomplete';

/** Pro subscription as described by the server (App\Services\SubscriptionSync::summary). */
export type Subscription = {
  plan: 'pro';
  interval: 'monthly' | 'yearly';
  status: string;
  state: SubscriptionState;
  trial_ends_at: string | null;
  ends_at: string | null;
  renews_at: string | null;
};

// Helper for checking email verification
export const isEmailVerified = (user: User) => user.email_verified_at !== null;

type AuthState = {
  user: User | null;
  isLoading: boolean;
  // Server master switch (GET /api/config) — is the accounts/tiers system live at all.
  rolesEnabled: boolean;
  refreshUser: () => Promise<void>;
  register: (name: string, email: string, password: string, claimId?: string) => Promise<void>;
  login: (email: string, password: string, claimId?: string, remember?: boolean) => Promise<void>;
  logout: () => Promise<void>;
  deleteAccount: () => Promise<void>;
  requestEmailVerification: () => Promise<EmailVerificationOutcome>;
  requestPasswordReset: (email: string) => Promise<void>;
  confirmPasswordReset: (email: string, token: string, password: string) => Promise<void>;
};

/** What the server actually did when asked for a verification email. */
export type EmailVerificationOutcome = 'sent' | 'verified_without_email' | 'already_verified';

const AuthContext = createContext<AuthState | null>(null);

// CSRF token helpers
function getCsrfToken(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

async function ensureCsrfCookie(force = false): Promise<void> {
  // `force` bypasses the "already have a token" shortcut so a stale token can be
  // replaced (see the 419 retry in sanctumFetch).
  if (!force && getCsrfToken()) return;
  await fetch('/sanctum/csrf-cookie', {
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest'
    }
  });
}

// Sanctum-aware fetch wrapper
async function sanctumFetch(path: string, options: RequestInit = {}): Promise<Response> {
  // Normalize method and determine if it's a write request (needs CSRF)
  const method = (options.method || 'GET').toUpperCase();
  const isWriteRequest = !['GET', 'HEAD', 'OPTIONS'].includes(method);

  // Ensure CSRF cookie exists for mutating requests
  if (isWriteRequest) {
    await ensureCsrfCookie();
  }

  // Reads the current XSRF token each call so a post-419 refresh takes effect.
  const send = (): Promise<Response> => {
    const csrfToken = getCsrfToken();
    return fetch(path, {
      ...options,
      method,
      credentials: 'include',
      // Only use manual redirect for writes (POST/DELETE) to prevent CORS redirect issues
      ...(isWriteRequest ? { redirect: 'manual' as const } : {}),
      headers: {
        // Only set Content-Type for requests with body (writes)
        ...(isWriteRequest ? { 'Content-Type': 'application/json' } : {}),
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',  // Fortify checks this for JSON response
        ...(csrfToken ? { 'X-XSRF-TOKEN': csrfToken } : {}),
        ...(options.headers || {})
      }
    });
  };

  let resp = await send();

  // A 419 means the CSRF token was stale (the session rotated or expired). Without
  // this retry the user is locked out of login/register until they manually clear
  // cookies — because ensureCsrfCookie() trusts any existing token. Refresh once and
  // resend so a stale token self-heals.
  if (isWriteRequest && resp.status === 419) {
    await ensureCsrfCookie(true);
    resp = await send();
  }

  // Detect opaqueredirect (status 0) - happens when server returns 302 despite JSON headers
  if (resp.type === 'opaqueredirect' || (isWriteRequest && resp.status === 0)) {
    throw new Error('Server returned a redirect. Check Fortify configuration or Accept/X-Requested-With headers.');
  }

  return resp;
}

async function postJson(path: string, body?: Record<string, unknown>): Promise<Response> {
  return sanctumFetch(path, {
    method: 'POST',
    body: body ? JSON.stringify(body) : undefined
  });
}

async function getJson(path: string): Promise<Response> {
  return sanctumFetch(path, { method: 'GET' });
}

/**
 * Set (or clear) the tier an admin acts as, on the SERVER.
 *
 * Exported because this is now the real switch: it changes what the backend permits, not just
 * what the UI draws. The old localStorage-only simulation gave an admin a Free-looking UI in
 * front of a server that still granted them everything.
 */
export async function patchActingTier(tier: TierValue | null): Promise<Response> {
  return sanctumFetch('/api/me/acting-tier', {
    method: 'PATCH',
    body: JSON.stringify({ acting_tier: tier }),
  });
}

async function deleteJson(path: string): Promise<Response> {
  return sanctumFetch(path, { method: 'DELETE' });
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [rolesEnabled, setRolesEnabled] = useState<boolean>(() => getCachedRolesEnabled());

  const fetchMe = useCallback(async(): Promise<User | null> => {
    const resp = await getJson('/api/me');
    if (!resp.ok) return null;
    return await resp.json();
  }, []);

  const refreshUser = useCallback(async(): Promise<void> => {
    const me = await fetchMe();
    setUser(me);
  }, [fetchMe]);

  // Boot-time init: check if already logged in via session
  useEffect(() => {
    let cancelled = false;
    (async() => {
      try {
        const me = await fetchMe();
        if (cancelled) return;
        setUser(me);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchMe]);

  // Boot-time init: load the public master switch (anonymous-readable; warm from cache).
  useEffect(() => {
    let cancelled = false;
    (async() => {
      try {
        const cfg = await fetchAppConfig();
        if (!cancelled) setRolesEnabled(cfg.roles_enabled);
      } catch (_e) {
        // Keep the cached/default value on failure.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // After login/register, a "Get Pro" pressed just before (Pricing, signed out) carries on to Stripe
  // Checkout. Only a fresh one: the intent expires and is dropped when the dialog closes
  // (services/billingIntent), so a sign-in anywhere else can never pick up an old click.
  const resumeBillingIntent = useCallback(async() => {
    const intent = takeBillingIntent();
    if (!intent) return;
    try {
      const result = await createCheckout(intent.plan, intent.interval);
      if (result.redirect === 'portal') {
        // Already subscribed, so there is nothing to buy. The server offers its billing portal, but
        // that answers a question they did not ask — show their account and say so instead.
        requestAccount('already-pro');
        return;
      }
      if (result.url) {
        window.location.assign(result.url);
      }
    } catch (_e) {
      // Non-critical — user can retry from Pricing page by clicking Get Pro again
    }
  }, []);

  const register = useCallback(async(name: string, email: string, password: string, claimId?: string) => {
    const body: Record<string, string | boolean> = {
      name,
      email,
      password,
      password_confirmation: password,
      // New accounts stay signed in across restarts, same as "Remember me" on login.
      remember: true
    };
    if (claimId) body.claim = claimId;

    const resp = await postJson('/register', body);
    if (!resp.ok) {
      const data = await resp.json().catch(() => ({}));
      if (data?.errors) {
        // Laravel validation errors
        const firstError = Object.values(data.errors)[0];
        throw new Error(Array.isArray(firstError) ? firstError[0] : String(firstError));
      }
      throw new Error('Registration failed');
    }

    const data = await resp.json().catch(() => ({}));

    // After registration, fetch user (now logged in via session)
    const me = await fetchMe();
    setUser(me);

    // Handle post-registration redirect (e.g., after claiming a project)
    if (data?.redirect_url) {
      window.location.assign(data.redirect_url);
      return;
    }

    // Resume billing intent if user signed up from Pricing page
    await resumeBillingIntent();
  }, [fetchMe, resumeBillingIntent]);

  const login = useCallback(async(email: string, password: string, claimId?: string, remember = true) => {
    // `remember` asks Fortify for Laravel's long-lived remember-me cookie, so the user stays
    // signed in after the 120-minute session ends or the browser/computer restarts.
    const body: Record<string, string | boolean> = { email, password, remember };
    if (claimId) body.claim = claimId;

    const resp = await postJson('/login', body);
    if (!resp.ok) {
      const data = await resp.json().catch(() => ({}));
      if (data?.message) {
        throw new Error(data.message);
      }
      throw new Error('Login failed');
    }

    const data = await resp.json().catch(() => ({}));

    // Fetch user data after login
    const me = await fetchMe();
    setUser(me);

    // Handle post-login redirect (e.g., after claiming a project)
    if (data?.redirect_url) {
      window.location.assign(data.redirect_url);
      return;
    }

    // Resume billing intent if user logged in from Pricing page
    await resumeBillingIntent();
  }, [fetchMe, resumeBillingIntent]);

  const logout = useCallback(async() => {
    await postJson('/logout');
    setUser(null);
  }, []);

  const deleteAccount = useCallback(async() => {
    const resp = await deleteJson('/api/me');
    if (!resp.ok) throw new Error('Failed to delete account');
    setUser(null);
  }, []);

  const requestEmailVerification = useCallback(async(): Promise<EmailVerificationOutcome> => {
    // Our own endpoint, not Fortify's: Fortify always answers the same way, so the caller could
    // only ever claim an email was sent. On a deployment that cannot send mail that is untrue, and
    // the server is the only side that knows.
    const resp = await postJson('/api/email/verification-notification');
    if (!resp.ok) throw new Error('Failed to send verification email');
    const data = await resp.json().catch(() => ({}));
    return data?.outcome === 'verified_without_email' || data?.outcome === 'already_verified'
      ? data.outcome
      : 'sent';
  }, []);

  const requestPasswordReset = useCallback(async(email: string) => {
    const resp = await postJson('/forgot-password', { email });
    if (!resp.ok) throw new Error('Failed to request password reset');
  }, []);

  const confirmPasswordReset = useCallback(async(email: string, token: string, password: string) => {
    const resp = await postJson('/reset-password', {
      email,
      token,
      password,
      password_confirmation: password
    });
    if (!resp.ok) throw new Error('Failed to reset password');
    // User should now log in with new password
    setUser(null);
  }, []);

  const value = useMemo<AuthState>(() => ({
    user,
    isLoading,
    rolesEnabled,
    refreshUser,
    register,
    login,
    logout,
    deleteAccount,
    requestEmailVerification,
    requestPasswordReset,
    confirmPasswordReset
  }), [
    user,
    isLoading,
    rolesEnabled,
    refreshUser,
    register,
    login,
    logout,
    deleteAccount,
    requestEmailVerification,
    requestPasswordReset,
    confirmPasswordReset
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('AuthContextMissing');
  return ctx;
}

// Optional access: returns null when AuthProvider is not mounted.
export function useMaybeAuth() {
  return useContext(AuthContext);
}
